import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import { disableAiForSession } from './availability';
/**
 * One streamed AI response (summary or answer) read from the backend's NDJSON
 * body. Starting a new request aborts the running one, which also stops the
 * upstream completion from billing.
 */
export default class AiStream {
  constructor(endpoint, retainWhileWaiting) {
    this.endpoint = endpoint;
    this.retainWhileWaiting = retainWhileWaiting;
    _defineProperty(this, "key", null);
    _defineProperty(this, "status", 'idle');
    _defineProperty(this, "text", '');
    _defineProperty(this, "sources", []);
    /**
     * `text` still belongs to the previous request. Kept on screen (dimmed)
     * until the new request produces output of its own.
     */
    _defineProperty(this, "retained", false);
    _defineProperty(this, "controller", null);
    _defineProperty(this, "nextSources", []);
  }
  isRunning() {
    return this.status === 'waiting' || this.status === 'streaming';
  }
  start(key, request) {
    this.cancel();
    const controller = new AbortController();
    this.controller = controller;
    this.key = key;
    this.status = 'waiting';
    this.nextSources = [];
    if (this.retainWhileWaiting && this.text) {
      this.retained = true;
    } else {
      this.retained = false;
      this.text = '';
      this.sources = [];
    }
    m.redraw();
    void this.run(controller, request);
  }

  /**
   * Abort the running request, if any. Returns whether anything changed.
   */
  cancel() {
    if (!this.controller) return false;
    this.controller.abort();
    this.controller = null;
    if (this.isRunning()) this.status = 'aborted';
    return true;
  }

  /**
   * Abort and forget everything. Returns whether anything changed.
   */
  reset() {
    const changed = this.cancel() || this.status !== 'idle' || this.text !== '';
    this.key = null;
    this.status = 'idle';
    this.text = '';
    this.sources = [];
    this.nextSources = [];
    this.retained = false;
    return changed;
  }
  async run(controller, request) {
    const owns = () => this.controller === controller;
    let reader = null;
    try {
      const response = await fetch(app.forum.attribute('apiUrl') + '/glowingblue-search/' + this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/x-ndjson',
          'X-CSRF-Token': app.session.csrfToken
        },
        credentials: 'same-origin',
        body: JSON.stringify(request),
        signal: controller.signal
      });
      if (!owns()) return;

      // Branch on the status alone: 401 and 429 come from core's error
      // handler with a JSON:API body, the others with `{"error": code}`.
      if (!response.ok || !response.body) {
        if (response.status === 402 || response.status === 503) disableAiForSession();
        this.settle(response.status === 429 ? 'rate_limited' : 'error');
        return;
      }
      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffered = '';
      let finished = false;
      while (!finished) {
        var _lines$pop;
        let chunk;
        try {
          chunk = await reader.read();
        } catch (_unused) {
          if (owns()) this.settle('interrupted');
          return;
        }
        if (!owns()) return;
        const _chunk = chunk,
          done = _chunk.done,
          value = _chunk.value;
        if (done) break;
        buffered += decoder.decode(value, {
          stream: true
        });

        // Everything before the last newline is complete; the rest waits for
        // the next chunk.
        const lines = buffered.split('\n');
        buffered = (_lines$pop = lines.pop()) != null ? _lines$pop : '';
        for (const line of lines) {
          if (this.apply(line)) {
            finished = true;
            break;
          }
        }
        m.redraw();
      }
      if (!finished && !this.apply(buffered)) {
        // Closed without a `done` frame: whatever arrived may be cut short.
        this.settle('interrupted');
      }
    } catch (_unused2) {
      if (owns()) this.settle('error');
    } finally {
      var _reader;
      if (owns()) this.controller = null;
      (_reader = reader) == null || _reader.cancel().catch(() => {});
      m.redraw();
    }
  }

  /**
   * Apply one NDJSON line. Returns true on the `done` terminator.
   */
  apply(line) {
    var _frame$sources;
    if (!line.trim()) return false;
    let frame;
    try {
      frame = JSON.parse(line);
    } catch (_unused3) {
      return false;
    }
    switch (frame.type) {
      case 'meta':
        this.nextSources = (_frame$sources = frame.sources) != null ? _frame$sources : [];
        if (!this.retained) this.sources = this.nextSources;
        if (frame.empty) this.settle('empty');
        return false;
      case 'delta':
        if (!this.isRunning()) return false;
        this.takeOver();
        this.text += frame.content;
        this.status = 'streaming';
        return false;
      case 'no_answer':
        this.settle('no_answer');
        this.sources = [];
        return false;
      case 'error':
        this.settle('error');
        return false;
      case 'done':
        if (this.status === 'streaming') this.status = 'done';else if (this.status === 'waiting') this.settle('error');
        return true;
      default:
        return false;
    }
  }
  takeOver() {
    if (!this.retained) return;
    this.retained = false;
    this.text = '';
    this.sources = this.nextSources;
  }
  settle(status) {
    this.takeOver();
    this.text = '';
    this.status = status;
  }
}
flarum.reg.add('glowingblue-search', 'forum/ai/AiStream', AiStream);